import React, { useState } from 'react';
import {
  X,
  History,
  Activity,
  Clock,
  RotateCcw,
  CheckCircle2,
  Lock,
  Unlock,
  Share2,
  Trash2,
  Edit3,
  FilePlus,
  ShieldCheck,
  CreditCard,
  AlertCircle,
} from 'lucide-react';

export default function CardActivityModal({
  open,
  onClose,
  card,
  onRestoreVersion,
}) {
  const [activeTab, setActiveTab] = useState('versions'); // 'versions' | 'audit'
  const [selectedVersionIndex, setSelectedVersionIndex] = useState(null);
  const [restoring, setRestoring] = useState(false);

  if (!open || !card) return null;

  const f = card.fields || card;

  const versions =
    Array.isArray(card.versions) && card.versions.length > 0
      ? card.versions
      : [
          {
            version: 1,
            savedAt: card.updated_at || card.created_at || new Date().toISOString(),
            title: card.cardName || card.title || 'Untitled Card',
            summary: 'Initial payment card record created',
            fields: {
              cardName: card.cardName || card.title,
              issuer: f.issuer,
              cardholderName: f.cardholderName,
              cardLast4: (f.cardNumber || '').replace(/\s+/g, '').slice(-4),
              expiry: `${f.expiryMonth || 'MM'}/${f.expiryYear || 'YYYY'}`,
            },
          },
        ];

  const auditLog =
    Array.isArray(card.auditLog) && card.auditLog.length > 0
      ? card.auditLog
      : [
          {
            action: 'Created',
            actor: 'You (Current Device)',
            timestamp: card.created_at || new Date().toISOString(),
            details: 'Secure encrypted payment card created',
          },
          ...(card.updated_at && card.updated_at !== card.created_at
            ? [
                {
                  action: 'Updated',
                  actor: 'You (Current Device)',
                  timestamp: card.updated_at,
                  details: 'Card details and billing information re-encrypted',
                },
              ]
            : []),
          ...(card.attachments && card.attachments.length > 0
            ? [
                {
                  action: 'Card Scans Attached',
                  actor: 'You (Current Device)',
                  timestamp: card.attachments[0].uploadedAt || card.updated_at || new Date().toISOString(),
                  details: `${card.attachments.length} encrypted card photo scan(s) uploaded`,
                },
              ]
            : []),
        ];

  const selectedVersion =
    selectedVersionIndex !== null ? versions[selectedVersionIndex] : versions[versions.length - 1];

  const handleRestore = async () => {
    if (!selectedVersion || !onRestoreVersion) return;
    setRestoring(true);
    try {
      await onRestoreVersion(card, selectedVersion);
      onClose();
    } catch (err) {
      console.error('Error restoring card version:', err);
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="card-drawer-wrapper" role="presentation">
      <div className="card-drawer-overlay animate-fade-in" onClick={onClose} aria-hidden="true" />
      <aside
        className="card-drawer card-activity-drawer ios-card-drawer identity-activity-drawer animate-slide-left"
        role="dialog"
        aria-modal="true"
        aria-label="Card Activity & Version History"
      >
        {/* Header */}
        <div className="card-drawer-header modal-header identity-activity-header">
          <div className="identity-activity-title-group">
            <div className="identity-header-avatar" style={{ background: 'rgba(59, 130, 246, 0.15)', color: '#3b82f6' }}>
              <History size={18} />
            </div>
            <div className="identity-activity-titles">
              <h3 className="modal-title">Activity & Version History</h3>
              <p className="modal-subtitle">
                Inspect audit events and rollback changes for "{card.cardName || card.title}"
              </p>
            </div>
          </div>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close activity dialog"
          >
            <X size={16} />
          </button>
        </div>

        {/* Tab switcher (iOS Segmented Control) */}
        <div className="card-segmented-control identity-activity-tabs">
          <button
            type="button"
            className={`card-segmented-btn ${activeTab === 'versions' ? 'active' : ''}`}
            onClick={() => setActiveTab('versions')}
          >
            <Clock size={13} />
            <span>Version History ({versions.length})</span>
          </button>
          <button
            type="button"
            className={`card-segmented-btn ${activeTab === 'audit' ? 'active' : ''}`}
            onClick={() => setActiveTab('audit')}
          >
            <Activity size={13} />
            <span>Audit Trail ({auditLog.length})</span>
          </button>
        </div>

        {/* Content Body */}
        <div className="card-drawer-body modal-body identity-activity-body">
          {activeTab === 'versions' && (
            <div className="identity-versions-layout card-activity-versions-layout">
              {/* Version list column */}
              <div className="identity-versions-sidebar">
                <div className="identity-versions-list">
                  {versions.map((v, idx) => {
                    const isSelected =
                      selectedVersionIndex === idx ||
                      (selectedVersionIndex === null && idx === versions.length - 1);
                    return (
                      <button
                        key={idx}
                        type="button"
                        className={`identity-version-item ${isSelected ? 'active' : ''}`}
                        onClick={() => setSelectedVersionIndex(idx)}
                      >
                        <div className="identity-version-num">
                          <span>v{v.version || idx + 1}</span>
                          {idx === versions.length - 1 && (
                            <span className="badge badge-primary">Current</span>
                          )}
                        </div>
                        <div className="identity-version-time">
                          {new Date(v.savedAt).toLocaleString([], {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </div>
                        <div className="identity-version-summary">
                          {v.summary || 'Card details modified'}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Version preview column */}
              <div className="identity-version-preview">
                {selectedVersion ? (
                  <div className="identity-version-preview-content">
                    <div className="identity-version-preview-header">
                      <div>
                        <h4>
                          Snapshot v
                          {selectedVersion.version ||
                            (selectedVersionIndex !== null ? selectedVersionIndex + 1 : versions.length)}
                        </h4>
                        <span className="text-secondary text-sm">
                          Captured on {new Date(selectedVersion.savedAt).toLocaleString()}
                        </span>
                      </div>
                      {selectedVersionIndex !== null && selectedVersionIndex !== versions.length - 1 && (
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm identity-rollback-btn"
                          onClick={handleRestore}
                          disabled={restoring}
                        >
                          <RotateCcw size={13} />
                          <span>{restoring ? 'Restoring...' : 'Rollback to this version'}</span>
                        </button>
                      )}
                    </div>

                    <div className="identity-version-preview-box">
                      <div className="preview-field-row">
                        <span className="preview-label">Card Name:</span>
                        <span className="preview-value">{selectedVersion.title || card.cardName}</span>
                      </div>
                      {selectedVersion.fields && (
                        <>
                          {selectedVersion.fields.issuer && (
                            <div className="preview-field-row">
                              <span className="preview-label">Issuer:</span>
                              <span className="preview-value">{selectedVersion.fields.issuer}</span>
                            </div>
                          )}
                          {selectedVersion.fields.cardholderName && (
                            <div className="preview-field-row">
                              <span className="preview-label">Cardholder:</span>
                              <span className="preview-value">{selectedVersion.fields.cardholderName}</span>
                            </div>
                          )}
                          {selectedVersion.fields.cardLast4 && (
                            <div className="preview-field-row">
                              <span className="preview-label">Card Number:</span>
                              <span className="preview-value">
                                •••• •••• •••• {selectedVersion.fields.cardLast4}
                              </span>
                            </div>
                          )}
                          {selectedVersion.fields.expiry && (
                            <div className="preview-field-row">
                              <span className="preview-label">Expiry:</span>
                              <span className="preview-value">{selectedVersion.fields.expiry}</span>
                            </div>
                          )}
                        </>
                      )}
                      <div className="identity-version-security-note">
                        <ShieldCheck size={14} className="security-note-icon" />
                        <span>Security note: CVV and PIN are never exposed in plaintext audit logs.</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="identity-version-empty">
                    <History size={36} opacity={0.3} />
                    <p>Select a version snapshot to preview changes.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'audit' && (
            <div className="identity-audit-list">
              {auditLog.map((item, idx) => (
                <div key={idx} className="identity-audit-item">
                  <div className="identity-audit-icon-box">
                    {item.action === 'Created' && <FilePlus size={16} className="text-success" />}
                    {item.action === 'Updated' && <Edit3 size={16} className="text-primary" />}
                    {item.action === 'Card Scans Attached' && <ShieldCheck size={16} className="text-info" />}
                    {item.action === 'Shared' && <Share2 size={16} className="text-warning" />}
                    {item.action === 'Locked' && <Lock size={16} className="text-secondary" />}
                    {!['Created', 'Updated', 'Card Scans Attached', 'Shared', 'Locked'].includes(item.action) && (
                      <CheckCircle2 size={16} className="text-primary" />
                    )}
                  </div>
                  <div className="identity-audit-content">
                    <div className="identity-audit-row">
                      <span className="identity-audit-action">{item.action}</span>
                      <span className="identity-audit-time">
                        {new Date(item.timestamp).toLocaleString([], {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                    <p className="identity-audit-details">{item.details}</p>
                    <span className="identity-audit-actor">{item.actor}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="card-drawer-footer modal-footer identity-activity-footer">
          <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
            Close
          </button>
        </div>
      </aside>
    </div>
  );
}
